import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { from, Observable, throwError } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {

  constructor(private toastr: ToastrService) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('ErrorInterceptor:', error);

        if (error.status === 401) {
          return throwError(() => error);
        }

        return from(this.getErrorPayload(error)).pipe(
          mergeMap((payload) => {
            this.handleErrorByStatus(error, payload);
            return throwError(() => error);
          })
        );
      })
    );
  }

  private async getErrorPayload(error: HttpErrorResponse): Promise<any> {
    const raw = error.error;

    if (raw instanceof Blob) {
      const text = await raw.text();
      return this.parseErrorPayload(text);
    }

    return this.parseErrorPayload(raw);
  }

  private parseErrorPayload(raw: any): any {
    if (!raw) {
      return {};
    }

    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch {
        return { message: raw };
      }
    }

    return raw;
  }

  private handleErrorByStatus(error: HttpErrorResponse, errObj: any): void {
    console.log('errObj', errObj);

    const errores = this.getErrorList(errObj);
    const mensaje = this.getErrorMessage(errObj);
    const titulo = errObj.title || '!ERROR!';
    console.log('mensaje', mensaje);
    console.log('errores', error);

    switch (error.status) {
      case 0:
        this.toastr.error(
          'No hay conexión con el servidor. Verifica tu red.',
          'Error de conexión'
        );
        break;

      case 400:
        if (errores.length > 0) {
          this.showErrorList(errores, mensaje || 'Solicitud incorrecta');
        } else {
          this.toastr.error(mensaje, 'Solicitud incorrecta', {
            timeOut: 6000
          });
        }
        break;

      case 403:
        this.toastr.warning(mensaje, 'Acceso denegado', {
          timeOut: 6000
        });
        break;

      case 404:
        this.toastr.warning(mensaje, 'Recurso no encontrado', {
          timeOut: 6000
        });
        break;

      case 422:
        if (errores.length > 0) {
          this.showErrorList(errores, 'Errores de validación');
        } else {
          this.toastr.error(mensaje, 'Error de validación');
        }
        break;


      case 500:
        this.toastr.error(
          mensaje,
          'Error interno del servidor'
        );
        break;

      default:
        this.toastr.error(
          mensaje,
          titulo
        );
        break;
    }
  }

  private getErrorList(errObj: any): string[] {
    const errors = errObj?.errors || errObj?.detail;

    if (Array.isArray(errors)) {
      return errors
        .map((errorItem) => this.formatErrorItem(errorItem))
        .filter(Boolean);
    }

    if (errors && typeof errors === 'object') {
      return Object.entries(errors)
        .map(([field, value]) => {
          if (Array.isArray(value)) {
            return value.map((item) => `${field}: ${this.formatErrorItem(item)}`).join('<br>');
          }

          return `${field}: ${this.formatErrorItem(value)}`;
        })
        .filter(Boolean);
    }

    return [];
  }

  private getErrorMessage(errObj: any): string {
    if (typeof errObj?.message === 'string') {
      return errObj.message;
    }

    if (typeof errObj?.detail === 'string') {
      return errObj.detail;
    }

    if (typeof errObj?.error === 'string') {
      return errObj.error;
    }

    return 'Error desconocido';
  }

  private formatErrorItem(errorItem: any): string {
    if (!errorItem) {
      return '';
    }

    if (typeof errorItem === 'string') {
      return errorItem;
    }

    if (typeof errorItem === 'object') {
      if (errorItem.msg) {
        return errorItem.msg;
      }

      if (errorItem.message) {
        return errorItem.message;
      }
    }

    return String(errorItem);
  }

  private showErrorList(errores: string[], title: string): void {
    const listaErrores = errores.map(e => `• ${e}`).join('<br>');

    this.toastr.error(listaErrores, title, {
      enableHtml: true,
      timeOut: 20000,
      extendedTimeOut: 5000,
      closeButton: true,
      progressBar: true,
    });
  }
}
